import { BusinessDetails, LegalLinks } from "components";
import { useBrand } from "hooks/useBrand";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Paths from "routes/Paths";

import type { FooterColumn, FooterLink } from "./landingPage.types";
import NewsletterForm from "./NewsletterForm";

import styles from "./LandingFooter.module.css";

/**
 * Only destinations that actually exist are listed. The landing page is shown to signed-out
 * visitors, and Explore/Plans redirect them back to the landing page, so those two go through the
 * sign-in page with a redirect - the visitor still ends up on the page they clicked.
 *
 * In-page sections are routed like any other destination rather than left as bare anchors, so that
 * ScrollToTop performs the jump once the layout has settled.
 *
 * Labels live under `footer.columns` in the landingPage namespace; only the ordering and
 * destination are held here.
 */
const footerColumns: FooterColumn[] = [
	{
		key: "product",
		links: [
			{ labelKey: "features", target: "#features" },
			{ labelKey: "pricing", target: Paths.plans }
		]
	},
	{
		key: "explore",
		links: [
			{ labelKey: "trendingTrees", target: "#trending" },
			{ labelKey: "publicTrees", target: Paths.explore }
		]
	},
	{
		key: "account",
		links: [
			{ labelKey: "createAccount", target: Paths.signUp },
			{ labelKey: "signIn", target: Paths.signIn }
		]
	}
];

const LandingFooter = () => {
	const { t } = useTranslation(["landingPage", "common"]);
	const { brand } = useBrand();

	const renderFooterLink = (columnKey: string, link: FooterLink) => (
		<Link key={link.labelKey} className={styles.link} to={link.target}>
			{t(`landingPage:footer.columns.${columnKey}.${link.labelKey}`)}
		</Link>
	);

	return (
		<footer className={styles.footer} id="footer">
			<div className={styles.inner}>
				<section className={styles.brand}>
					<div className={styles.logo}>
						<img alt="" src="/assets/landing/main-logo.png" />
						<span>{brand}</span>
					</div>
					<div aria-hidden="true" className={styles.flourish} />
					<p className={styles.tagline}>
						{t("landingPage:footer.taglineLine1")}
						<br />
						{t("landingPage:footer.taglineLine2")}
					</p>
					{/* Who runs the site and how to reach them, where reviewers and visitors look first. */}
					<BusinessDetails className={styles.businessDetails} />
				</section>

				<nav aria-label={t("landingPage:footer.navLabel")} className={styles.columns}>
					{footerColumns.map((column: FooterColumn) => (
						<section key={column.key} className={styles.column}>
							<h2>{t(`landingPage:footer.columns.${column.key}.title`)}</h2>
							{column.links.map((link: FooterLink) => renderFooterLink(column.key, link))}
						</section>
					))}
				</nav>

				<section className={styles.newsletter}>
					<h2>{t("landingPage:footer.newsletter.heading")}</h2>
					<p>{t("landingPage:footer.newsletter.description")}</p>
					<NewsletterForm />
				</section>
			</div>

			<div className={styles.legal}>
				<span>{t("common:copyright")}</span>
				{/* Shared list rather than a local one: payment-gateway reviews look for Contact Us and
				    the Refund & Cancellation Policy by their exact wording, and both were missing here. */}
				<nav aria-label={t("common:sitePagesLabel")} className={styles.legalNav}>
					<LegalLinks />
				</nav>
			</div>
		</footer>
	);
};

export default LandingFooter;
